// Mercapoli - Política de Privacidad
import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Footer from '../../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mercadelivery-1/artifacts/5y038jeg_icono_mercapoli.png";

const PoliticaPrivacidad = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Política de Privacidad | Mercapoli</title>
        <meta name="description" content="Conoce cómo Mercapoli protege y utiliza tus datos personales." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Mercapoli" className="w-10 h-10" />
              <span className="font-heading text-xl font-bold">Mercapoli</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-brand-primary/10 flex items-center justify-center">
              <Shield className="h-8 w-8 text-brand-primary" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">
              Política de Privacidad
            </h1>
            <p className="text-sm text-muted-foreground">
              Última actualización: Enero 2026
            </p>
          </div>

          <div className="prose prose-lg dark:prose-invert mx-auto space-y-8">
            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Nuestro Compromiso</h2>
              <p>
                Mercapoli se compromete a proteger la privacidad de los datos personales de 
                sus usuarios. Esta política describe cómo recopilamos, usamos y protegemos 
                su información.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Información que Recopilamos</h2>
              <p>La información recopilada se utiliza para:</p>
              <ul className="list-disc list-inside space-y-2 mt-4">
                <li>Operar y mejorar la plataforma</li>
                <li>Mejorar la experiencia del usuario</li>
                <li>Procesar pagos y entregas</li>
                <li>Comunicación y soporte al cliente</li>
                <li>Enviar notificaciones relevantes sobre pedidos</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Tipos de Datos</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold mb-2">Datos de Identificación</h3>
                  <p className="text-muted-foreground">
                    Nombre, correo electrónico, número de teléfono, dirección de entrega.
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Datos de Uso</h3>
                  <p className="text-muted-foreground">
                    Historial de pedidos, preferencias, ubicación (con consentimiento).
                  </p>
                </div>
                <div>
                  <h3 className="font-bold mb-2">Datos Técnicos</h3>
                  <p className="text-muted-foreground">
                    Tipo de dispositivo, sistema operativo, dirección IP (para seguridad).
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Compartición de Datos</h2>
              <p>
                Mercapoli <strong>no vende ni comparte</strong> datos personales con terceros 
                sin autorización, salvo obligación legal o para completar el servicio de entrega 
                (por ejemplo, compartir dirección con el repartidor asignado).
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Seguridad</h2>
              <p>
                Implementamos medidas de seguridad técnicas y organizativas para proteger 
                sus datos contra acceso no autorizado, pérdida o alteración. Utilizamos 
                encriptación y protocolos seguros para la transmisión de información sensible.
              </p>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Sus Derechos</h2>
              <p>Usted tiene derecho a:</p>
              <ul className="list-disc list-inside space-y-2 mt-4">
                <li>Acceder a sus datos personales</li>
                <li>Rectificar información incorrecta</li>
                <li>Solicitar la eliminación de su cuenta</li>
                <li>Oponerse al procesamiento de sus datos</li>
                <li>Exportar sus datos en formato portable</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading text-xl font-bold mb-4">Contacto</h2>
              <p>
                Para ejercer sus derechos o consultas sobre privacidad:
                <br />
                <a href="mailto:soporte@mercapoli.com" className="text-brand-primary hover:underline">
                  soporte@mercapoli.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PoliticaPrivacidad;
